import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { LibraryFocusRail } from '@/features/libraries/LibraryFocusRail'
import { useAuth } from '@/lib/auth'
import { useTheme } from '@/lib/theme'
import { initials } from '@/lib/format'
import { NavIcon, type NavIconName } from '@/components/ui/NavIcons'
import { policyLinks } from '@/components/ui/PolicyLinks'
import styles from './AppShell.module.css'

interface NavItem {
  to: string
  label: string
  icon: NavIconName
  /**
   * The server-side admin area this screen reads.
   *
   * *** THIS HIDES DOORS; IT DOES NOT LOCK THEM ***
   * Every refusal is decided by the API from the JWT claim. Filtering here
   * only spares a Coordinator from clicking into a 403. A tampered copy of
   * the role in localStorage reveals nothing — the screens it unhides still
   * refuse to load.
   */
  area: string
}

/**
 * Navigation, grouped by what an admin is trying to DO.
 *
 * Not grouped by API resource: "Masters" holds the five configuration screens
 * that are set up once and rarely revisited, while daily work (bookings,
 * students, payments) sits at the top where it is reached most often.
 */
const NAV_GROUPS: Array<{ title: string | null; items: NavItem[] }> = [
  {
    title: null,
    // `/dashboard`, not `/` — the domain root serves the public marketing
    // site, so a link to `/` would take an admin out of the portal.
    items: [{ to: '/dashboard', label: 'Dashboard', icon: 'dashboard' , area: 'dashboard' }],
  },
  {
    title: 'Operations',
    items: [
      { to: '/bookings', label: 'Bookings', icon: 'bookings' , area: 'bookings' },
      { to: '/students', label: 'Students', icon: 'students' , area: 'students' },
      { to: '/mentors', label: 'Mentors', icon: 'mentors' , area: 'mentors' },
      { to: '/payments', label: 'Payments', icon: 'payments' , area: 'payments' },
      // Only meaningful on a cash client; the page explains itself and
      // shows nothing to approve on a gateway one.
      { to: '/cash-requests', label: 'Cash requests', icon: 'payments' , area: 'subscription-requests' },
      { to: '/feedback', label: 'Feedback', icon: 'feedback' , area: 'feedback' },
      { to: '/libraries', label: 'Libraries', icon: 'students' , area: 'libraries' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/categories', label: 'Categories', icon: 'categories' , area: 'categories' },
      { to: '/session-types', label: 'Session types', icon: 'sessionTypes' , area: 'session-types' },
      { to: '/pricing', label: 'Pricing rules', icon: 'pricing' , area: 'pricing-rules' },
      { to: '/fee-heads', label: 'Fee heads', icon: 'feeHeads' , area: 'fee-heads' },
      { to: '/chat-policy', label: 'Chat policy', icon: 'chatPolicy' , area: 'chat-policy' },
      // Sits with the other masters: a plan is configuration, not an operation.
      { to: '/subscription-plans', label: 'Subscription plans', icon: 'pricing' , area: 'subscription-plans' },
      { to: '/settings', label: 'Settings', icon: 'settings' , area: 'settings' },
      // Who else can sign in, and the only way to fix a locked-out
      // colleague without calling the platform team.
      { to: '/admins', label: 'Admin accounts', icon: 'students', area: 'admins' },
    ],
  },
]

/**
 * Which areas each staff role may reach.
 *
 * *** KEPT IN STEP WITH ADMIN_AREA_GRANTS ON THE SERVER ***
 * Two copies is a real cost, but the alternative is an extra request before
 * the first paint just to learn what to draw. If these ever disagree, the
 * server wins: a screen shown here that it refuses is a dead end, and one
 * hidden here that it allows is merely unreachable from the menu.
 *
 * A full ADMIN — and an account predating roles, whose admin_role is null —
 * is not listed, because it sees everything.
 */
const ROLE_AREAS: Record<string, ReadonlySet<string>> = {
  COORDINATOR: new Set([
    'dashboard',
    'bookings',
    'students',
    'mentors',
    'mentor-breaks',
    'feedback',
    'chat-threads',
    'subscriptions',
    // Running a reading room is day-to-day coordination. Mirrors the server
    // grant; the Accountant is deliberately not given it.
    'libraries',
  ]),
  ACCOUNTANT: new Set([
    'dashboard',
    'payments',
    'subscription-requests',
    'subscriptions',
    'subscription-plans',
    'fee-heads',
    'pricing-rules',
    'bookings',
  ]),
}

/**
 * What a student or mentor sees. Nothing from the admin menu.
 *
 * *** THE WHOLE ADMIN NAV IS STAFF-ONLY ***
 * `maySee` below answers "which admin areas may this STAFF member reach" —
 * it never asked whether the user is staff at all. A student has
 * `admin_role = null`, which that function reads as a legacy full admin, so
 * a student was shown Bookings, Payments and Admin accounts: eighteen links
 * that every one of them answers 403 to.
 *
 * The server was never at risk — `auth_policy.py` refuses them regardless.
 * What was at risk is the student's belief that the product works.
 */
const LEARNER_NAV: Array<{ title: string | null; items: NavItem[] }> = [
  {
    title: null,
    items: [
      /*
        *** SESSIONS LEAD, NOT THE LIBRARY ***
        One-to-one mentoring is the product; the library is sold alongside
        it and may not even be enabled. A student or mentor opening the
        portal is most often here to check their next session.
      */
      {
        to: '/sessions',
        label: 'My sessions',
        icon: 'bookings',
        // `area` is inert here: LEARNER_NAV is not passed through
        // `maySee`, which gates STAFF links by admin_role. A student has
        // no admin_role to gate on.
        area: 'library',
      },
      /*
        Both roles, unlike "Your plan" below — `/chat/threads` serves a
        student and a mentor alike, and the app gives both a Messages tab.
      */
      {
        to: '/messages',
        label: 'Messages',
        icon: 'chatPolicy',
        area: 'library',
      },
      { to: '/library', label: 'Library', icon: 'students', area: 'library' },
      {
        to: '/leaderboard',
        label: 'Leaderboard',
        // `dashboard` — a panel of figures, which is what the board is.
        // NavIconName has twelve values and none of them is "reports";
        // picking one from memory cost a build.
        icon: 'dashboard',
        area: 'library',
      },
    ],
  },
]

function maySee(adminRole: string | null | undefined, area: string): boolean {
  // Null means an account from before roles existed: a full admin.
  if (!adminRole) return true
  const areas = ROLE_AREAS[adminRole]
  // ADMIN, or a role this build does not recognise — show everything and let
  // the server refuse. Hiding the whole menu on an unknown value would make a
  // newer server look like a broken portal.
  if (!areas) return true
  return areas.has(area)
}

export function AppShell() {
  const { user, logout } = useAuth()
  const { pathname } = useLocation()

  /*
    *** INSIDE A LIBRARY ROOM THE SIDEBAR BECOMES A FOCUS RAIL ***
    One dark column, always: the logo stays, and the navigation is replaced
    by the room's own controls — the timer, camera, break and Leave.

    A student who came to study for three hours does not need Library and
    Leaderboard one mis-click away; "All rooms" is the deliberate way out.
    Matched on the path rather than passed down as a prop, because the room
    renders several levels below this inside <Outlet />.

    `/library` itself is the lobby and keeps the normal nav; only
    `/library/<id>` is a room.
  */
  const inRoom = /^\/library\/[^/]+/.test(pathname)

  /*
    *** TWO QUESTIONS, NOT ONE ***
    First: is this person staff at all? A student or a mentor gets the
    learner nav, whatever their admin_role happens to be.
    Then, for staff: which admin areas does their sub-role reach, so a
    Coordinator is never shown a screen that can only answer 403.
  */
  const isStaff =
    user?.role === 'TENANT_ADMIN' || user?.role === 'SUPER_ADMIN'

  const navGroups = isStaff
    ? NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => maySee(user?.admin_role, item.area)),
      })).filter((group) => group.items.length > 0)
    : /*
        *** "YOUR PLAN" IS A STUDENT'S LINK ***
        `LEARNER_NAV` serves students AND mentors, but a mentor has no
        subscription: `/me/subscription` is a student endpoint and the page
        would be inert for them. Added here rather than in the constant so
        the mentor's menu stays what it was.

        Hidden on a PER_SESSION client too — that is decided by the server
        (`subscription_billing`), which this component has not asked, so the
        link is shown and the page explains. A nav that flickered as a query
        resolved would be worse than a page with an honest answer.
      */
      LEARNER_NAV.map((group) => ({
        ...group,
        items: [
          ...group.items,
          /*
            *** MENTORS ONLY, AND HIGH UP ***
            The app gives Availability its own bottom-nav tab, second after
            Sessions — a mentor manages their diary far more often than
            anything else here. A student has no diary to manage.
          */
          ...(user?.role === 'MENTOR'
            ? [
                {
                  to: '/availability',
                  label: 'Availability',
                  icon: 'sessionTypes' as NavIconName,
                  area: 'library',
                },
              ]
            : []),
          // Students only — see above.
          ...(user?.role === 'MENTOR'
            ? []
            : [
                {
                  to: '/plan',
                  label: 'Your plan',
                  icon: 'payments' as NavIconName,
                  area: 'library',
                },
              ]),
          /*
            *** LAST, AND FOR BOTH ROLES ***
            The topbar name already links here, but that is a convention
            people have to know. A named row is where someone looks for
            their own account, and it costs one line.

            Bottom of the list because a profile is opened occasionally —
            above Sessions it would push the daily screens down.
          */
          {
            to: '/profile',
            label: 'My profile',
            icon: 'mentors' as NavIconName,
            area: 'library',
          },
        ],
      }))
  const { theme, setTheme } = useTheme()
  const [navOpen, setNavOpen] = useState(false)

  const cycleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')
  }

  const themeIcon = theme === 'light' ? '☀' : theme === 'dark' ? '☾' : '◐'
  const themeLabel =
    theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'

  return (
    <div className={styles.shell}>
      <aside
        className={`${styles.sidebar} ${navOpen ? styles.sidebarOpen : ''}`}
      >
        <div className={styles.brand}>
          {/*
            *** THE CLIENT'S OWN MARK, WHEN THEY HAVE ONE ***
            One portal deployment serves every client, so without this each
            of them sees our logo on every screen of what is, to them, their
            own admin system.

            Replaces the platform mark rather than sitting beside it: two
            logos in a 32px square reads as clutter, and the product name is
            already spelled out next to it.

            `onError` falls back to the platform mark. The image is served
            from the blob store, and a missing object would otherwise leave a
            broken-image icon in the sidebar of every page.
          */}
          {user?.tenant_logo_url ? (
            <span className={styles.brandMark}>
              <img
                src={user.tenant_logo_url}
                alt={user.tenant_name ?? 'Institute logo'}
                className={styles.brandLogo}
                onError={(event) => {
                  event.currentTarget.style.display = 'none'
                }}
              />
            </span>
          ) : (
          /* The platform's mark, replacing the "MC" text monogram — two
              overlapping rings, the shared area between them the session.
              Inline so it needs no asset request. */
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 100 100" fill="none">
              <path
                d="M50 27.48 A26 26 0 0 1 50 72.52 A26 26 0 0 1 50 27.48 Z"
                fill="#ffffff"
                fillOpacity="0.92"
              />
              <circle
                cx="37"
                cy="50"
                r="26"
                stroke="#ffffff"
                strokeOpacity="0.8"
                strokeWidth="7"
              />
              <circle cx="63" cy="50" r="26" stroke="#ffffff" strokeWidth="7" />
            </svg>
          </span>
          )}

          <span className={styles.brandText}>
            <span className={styles.brandName}>MentorsConnect</span>
            {/*
              *** WHOSE ACCOUNT AM I IN? ***
              An admin who works across more than one client has no other way
              to tell from inside the portal — every screen looks identical.

              Rendered only when the server sends it: a build newer than the
              server would otherwise show an empty line where a name belongs.
            */}
            {user?.tenant_name && (
              <span className={styles.brandTenant} title={user.tenant_name}>
                {user.tenant_name}
              </span>
            )}
          </span>
        </div>

        {inRoom ? (
          <LibraryFocusRail />
        ) : (
        <nav className={styles.nav} aria-label="Main">
          {navGroups.map((group, index) => (
            <div key={group.title ?? index} className={styles.navGroup}>
              {group.title && (
                <p className={styles.navGroupTitle}>{group.title}</p>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // Exact matching on the dashboard. It mattered more when the
                  // route was "/", which prefix-matches every path; harmless
                  // and still correct now that it is "/dashboard".
                  end={item.to === '/dashboard'}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                  }
                  onClick={() => setNavOpen(false)}
                >
                  <span className={styles.navIcon} aria-hidden="true">
                    <NavIcon name={item.icon} />
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        )}

        {/*
          The company that builds the platform, not the client using it.
          "Metaguard Mobitech" is the legal entity now named on the public
          site's privacy policy and terms, so the three agree.

          The year is computed, not typed: a hardcoded one silently goes stale
          every January and nobody notices for months.
        */}
        {!inRoom && (
          <div className={styles.sidebarFooter}>
            {/*
              *** POLICY LINKS BELONG INSIDE THE PORTAL TOO ***
              The app puts them in both sign-in screens AND both settings
              screens. The portal has no settings screen for students or
              mentors yet, so the sidebar — which every role sees on every
              page — carries them until one exists.

              `tenant_slug` rides on the session, so these are the
              INSTITUTE's own policies. An admin signing in at `/login` has
              one too; only a super admin does not, and then the fallback
              pair is correct.
            */}
            {/*
              *** ONE LINE UNTIL ASKED FOR ***
              Four links stacked above the copyright filled the bottom
              corner with three lines of small text (reported 2026-10-31).
              These are read once, if ever, so they fold behind a summary
              and the resting state is a single line.

              `<details>` rather than state: it is open/closed markup, the
              browser handles the keyboard and it works with no JavaScript.
            */}
            <details className={styles.policyBox}>
              <summary className={styles.policySummary}>
                Policies &amp; support
              </summary>
              <nav className={styles.policyNav} aria-label="Policies">
                {policyLinks(user?.tenant_slug).map((link) => (
                  <a
                    key={link.label}
                    className={styles.policyLink}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </details>

            <span className={styles.copyright}>
              Metaguard Mobitech &copy; {new Date().getFullYear()}
            </span>
          </div>
        )}
      </aside>

      {/* Tapping outside the drawer closes it on mobile. */}
      {navOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
        />
      )}

      <div className={styles.main}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setNavOpen((open) => !open)}
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
          >
            ☰
          </button>

          <div className={styles.topbarRight}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={cycleTheme}
              title={`Theme: ${themeLabel}`}
              aria-label={`Theme: ${themeLabel}. Click to change.`}
            >
              {themeIcon}
            </button>

            <div className={styles.userMenu}>
              {/*
                *** THE NAME IS THE WAY INTO THE PROFILE ***
                Where everyone already looks for their own account, and it
                costs no sidebar row. A link rather than a fifth nav item:
                a profile is opened occasionally, not navigated between.
              */}
              <NavLink to="/profile" className={styles.userLink}>
                <span className={styles.avatar} aria-hidden="true">
                  {initials(user?.full_name ?? user?.email)}
                </span>
                <div className={styles.userInfo}>
                  <span className={styles.userName}>
                    {user?.full_name ?? 'Admin'}
                  </span>
                  <span className={styles.userEmail}>{user?.email}</span>
                </div>
              </NavLink>
              <button
                type="button"
                className={styles.signOut}
                onClick={logout}
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
